import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import { STATUS, dayMonth, money } from "./campaignFormat";
import "../../../styles/Campaigns.css";

// Everything an advertiser bought in one checkout, as one thing.
//
// Before this the advertiser side had only a flat list of bookings: fourteen
// rows for one campaign across fourteen screens, with nothing saying they
// belonged together, and no way to see whether the campaign as a whole was
// running. The backend has grouped them since the campaigns table landed;
// nothing rendered it.
//
// Refreshed on an interval because the states behind it change without the
// viewer doing anything -- a host approves, a screen drops off the network.
const REFRESH_MS = 30000;

export default function CampaignList() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const token = localStorage.getItem("token");

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/campaigns`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Could not load your campaigns.");
            const data = await res.json();
            setCampaigns(data.campaigns || []);
            setError("");
        } catch (err) {
            setError(err.message || "Could not load your campaigns.");
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    if (loading) return <p className="wb-sub">Loading your campaigns…</p>;

    return (
        <section className="wb-stack">
            <div className="wb-page-head">
                <h2>Campaigns</h2>
                <Link to="/devices" className="wb-btn wb-btn--primary wb-btn--sm">
                    Book screens
                </Link>
            </div>

            {error && <p className="wb-note wb-note--danger">{error}</p>}

            {!campaigns.length && !error && (
                <div className="wb-card wb-empty">
                    <p className="wb-sub">
                        Nothing booked yet. Everything you buy in one checkout becomes a
                        campaign you can follow here.
                    </p>
                    <Link to="/devices" className="wb-btn wb-btn--primary">Browse screens</Link>
                </div>
            )}

            <div className="camp-list">
                {campaigns.map((c) => {
                    const status = STATUS[c.status] || { label: c.status, tone: "" };
                    const now = c.now || {};
                    const w = c.window;
                    return (
                        <Link
                            key={c.id}
                            to={`/customer/campaigns/${c.id}`}
                            className="camp-card"
                        >
                            <div className="camp-card__head">
                                <div>
                                    <h3 className="camp-card__name">{c.name}</h3>
                                    <p className="camp-screen__meta">
                                        {c.screens} screen{c.screens === 1 ? "" : "s"}
                                        {" · "}
                                        {c.locations} location{c.locations === 1 ? "" : "s"}
                                        {w && ` · ${dayMonth(w.starts_at)} – ${dayMonth(w.ends_at)}`}
                                    </p>
                                </div>
                                <span className={"wb-badge" + (status.tone ? ` wb-badge--${status.tone}` : "")}>
                                    {status.label}
                                </span>
                            </div>

                            {w && (
                                <div className="camp-progress" style={{ borderBottom: "none" }}>
                                    <div className="camp-progress__head">
                                        <span>
                                            {w.day ? `Day ${w.day} of ${w.days}` : `${w.days} day${w.days === 1 ? "" : "s"}`}
                                        </span>
                                        <span>Ends {dayMonth(w.ends_at)}</span>
                                    </div>
                                    <div className="camp-bar">
                                        <div
                                            className="camp-bar__fill"
                                            style={{ width: `${Math.round((w.elapsed_fraction || 0) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="camp-card__foot">
                                {/* Only worth a line when something is expected on screen
                                    right now; at 3am every campaign is quiet and saying so
                                    fourteen times is noise. */}
                                {now.expected_now > 0 && (
                                    <span className={now.healthy ? "camp-health--ok" : "camp-health--issue"}>
                                        {now.playing_now} of {now.expected_now} playing now
                                    </span>
                                )}
                                <span>{money(c.money && c.money.charged_cents)} spent</span>
                                {c.money && c.money.refunded_cents > 0 && (
                                    <span>{money(c.money.refunded_cents)} refunded</span>
                                )}
                                {c.money && c.money.refunds_outstanding > 0 && (
                                    <span className="camp-health--issue">
                                        {c.money.refunds_outstanding} refund
                                        {c.money.refunds_outstanding === 1 ? "" : "s"} being sorted out
                                    </span>
                                )}
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}
